import { PropsWithChildren } from 'react'

import { Tooltip } from 'antd'
import { isNil } from 'lodash'

import { useFormItemLayout } from '../hooks/useFormItemLayout'
import { useOverflow } from '../hooks/useOverflow'
import { BaseFormItemProps } from '../interface'
import { FeedbackPopover } from '../Popover'
import { useFormItemStyles } from '../style'

export const BaseFormItem = ({
  children,
  ...props
}: PropsWithChildren<BaseFormItemProps>) => {
  const formLayout = useFormItemLayout(props)
  const {
    className,
    style,
    label,
    layout,
    colon = true,
    asterisk,
    help,
    feedbackLayout = 'default',
    feedbackStatus,
    feedbackText,
    fullness,
    labelWidth,
    labelAlign,
    labelWrap,
    wrapperWidth,
    wrapperMinWidth
  } = formLayout

  const labelStyle = { ...formLayout.labelStyle }
  const wrapperStyle = { ...formLayout.wrapperStyle }

  // 固定宽度
  if (labelWidth) {
    labelStyle.width = labelWidth
  }
  if (wrapperWidth) {
    wrapperStyle.width = wrapperWidth
  }
  if (wrapperMinWidth) {
    wrapperStyle.minWidth = wrapperMinWidth
  }

  const { containerRef, contentRef, overflow } = useOverflow<
    HTMLDivElement,
    HTMLDivElement
  >()

  const componentCls = 'form-item'

  const { cx, styles } = useFormItemStyles({ componentCls })

  const labelRender = () => {
    return (
      label && (
        <div
          ref={containerRef}
          style={labelStyle}
          className={cx({
            [`${componentCls}-label`]: true
          })}
        >
          <div
            ref={contentRef}
            className={cx({
              [`${componentCls}-label-content`]: true,
              [`${componentCls}-label-content-overflow`]: overflow,
              [`${componentCls}-label-content-wrap`]: labelWrap,
              [`${componentCls}-label-content-align-${labelAlign}`]: true
            })}
          >
            {asterisk && <span className={`${componentCls}-asterisk`}>*</span>}
            <span className={`${componentCls}-label-text`}>
              {overflow ? (
                <Tooltip title={label} trigger='hover'>
                  {label}
                </Tooltip>
              ) : (
                label
              )}
            </span>
            {label && colon && (
              <span className={`${componentCls}-colon`}>:</span>
            )}
          </div>
        </div>
      )
    )
  }

  const controlRender = () => {
    return (
      <div
        style={wrapperStyle}
        className={cx({
          [`${componentCls}-control`]: true,
          [`${componentCls}-control-fullness`]: fullness
        })}
      >
        {children}
      </div>
    )
  }

  return (
    <div
      className={cx(
        styles,
        {
          [`${componentCls}`]: true,
          [`${componentCls}-layout-${layout}`]: true,
          [`${componentCls}-feedbackLayout-${feedbackLayout}`]: true,
          [`${componentCls}-${feedbackStatus}`]: !!feedbackStatus
        },
        className
      )}
      style={{
        gridColumn: `span ${props.gridSpan ?? 1}`,
        ...style
      }}
    >
      {labelRender()}
      {feedbackLayout === 'popover' ? (
        <FeedbackPopover content={!isNil(feedbackText) ? feedbackText : null}>
          {controlRender()}
        </FeedbackPopover>
      ) : (
        controlRender()
      )}
      <div className={`${componentCls}-help`}>{help}</div>
      <div className={`${componentCls}-tip`}>
        {!!feedbackText && feedbackLayout === 'default' && (
          <div
            className={cx({
              [`${componentCls}-feedback`]: true,
              [`${componentCls}-feedback-enter`]: true,
              [`${componentCls}-feedback-enter-active`]: true,
              [`${componentCls}-feedback-${feedbackStatus}`]: !!feedbackStatus
            })}
          >
            {feedbackText}
          </div>
        )}
      </div>
    </div>
  )
}
